import { cache } from "react";
import {
  clientApi,
  serverApi,
  handleApiError,
  rethrowWithApiErrorCode,
} from "./api-client";
import { createLogger } from "./logger";

const log = createLogger("ApiFunctions");
import {
  JobLandingPageSchema,
  JobStatus,
  JobRequest,
  PublishRequest,
  JobSchema,
  ImageUploadResponse,
  VideoUploadResponse,
  PaginatedResponse,
  JobListing,
  PaymentRequiredResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  CustomerPortalRequest,
  CustomerPortalResponse,
  UserResponse,
  UserUpdateRequest,
  SendApplicantEmailRequest,
  SendApplicantEmailResponse,
  Applicant,
  ApplicantWithJobInfo,
  SendCVReminderRequest,
  SendInterviewInviteRequest,
  SendInterviewInviteResponse,
  ActivateCareerPageRequest,
  ActivateCareerPageResponse,
  UpdateCareerPageSlugRequest,
  UpdateCareerPageSlugResponse,
  CareerPageInfoResponse,
  ContactPurchaseResponse,
  EmailSchema,
  ContactApi,
  AtsType,
  ATSPosition,
  ATSPublishedPosition,
  CallsheetRowSchema,
  SnoozeRequest,
  SnoozeResponse,
  LandingPageAnalytics,
  SearchResponse,
  SearchStatus,
  PublicSearchResponse,
  CVUploadLinkInfoResponse,
  FunnelQuestion,
  JobOpenCheck,
  JobOpenCheckOutcome,
} from "../types";

// Minimal response type for email verification
export interface EmailVerificationResponse {
  message: string;
  access_token: string;
  token_type?: string;
  // Backend returns full user object; we only need email optionally
  user: { email?: string } & Record<string, unknown>;
}

// ============================================================================
// PUBLIC API FUNCTIONS (No authentication required)
// ============================================================================

// Analytics tracking
export async function trackAnalyticsEvent(
  eventName: string,
  ga?: Record<string, unknown> | null,
  meta?: Record<string, unknown> | null,
  applicantEmail?: string | null,
  jobLandingPageId?: string | null,
  applicantId?: string | null
): Promise<void> {
  try {
    // Use Next.js API route instead of calling backend directly
    // This ensures proper enrichment with user agent, IP, etc.
    await clientApi.post(
      "/api/v1/analytics/track",
      {
        eventName,
        ga,
        meta,
        applicantEmail,
        jobLandingPageId,
        applicantId,
      },
      true
    ); // useNextApi = true to use Next.js API route
  } catch (error) {
    // Analytics failures shouldn't break the app, just log them
    log.warn("Analytics tracking failed", {
      eventName,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

// Get CV upload link info (public - for applicants to see their info and CV questions)
export async function getCVUploadLinkInfo(
  linkId: string
): Promise<CVUploadLinkInfoResponse> {
  try {
    return await clientApi.get<CVUploadLinkInfoResponse>(
      `/api/v1/cv/upload-link/${linkId}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Get job status
// Job status is now on the landing page - jobId IS the landing page ID
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  try {
    const landingPage = await clientApi.get<JobLandingPageSchema>(
      `/api/v1/job-landing-pages/${jobId}`
    );
    // Content generation is complete when compositeState has progressed past initial draft
    // This includes all intermediate states (processing, failed, and published)
    const completionStates = [
      "content_generating",
      "content_failed",
      "draft_ready",
      "slides_generating",
      "slides_failed",
      "slides_ready",
      "checkout_pending",
      "checkout_failed",
      "publishing",
      "publish_failed",
      "ad_creating",
      "ad_failed",
      "unpublishing",
      "published_free",
      "published_paid",
      "published_trial",
      "published_inactive",
      "published_canceling",
    ];
    const isContentComplete = completionStates.includes(
      landingPage.compositeState || ""
    );
    const isJobComplete =
      landingPage.jobStatus === "completed" || isContentComplete;

    return {
      id: landingPage.id || jobId,
      status: isJobComplete ? "completed" : landingPage.jobStatus || "pending",
      progress: landingPage.jobProgress || 0,
      message: landingPage.jobMessage || null,
      error: landingPage.jobError || null,
      // Return the landing page as result when completed so redirects work properly
      result: isJobComplete ? landingPage : null,
      taskStates: landingPage.taskStates || null,
      compositeState: landingPage.compositeState || null,
    };
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Get job status (authenticated)
// Job status is now on the landing page - jobId IS the landing page ID
export async function getJobStatusAuth(jobId: string): Promise<JobStatus> {
  try {
    const landingPage = await clientApi.getAuth<JobLandingPageSchema>(
      `/api/admin/job-landing-pages/${jobId}`,
      true
    );
    // Content generation is complete when compositeState has progressed past initial draft
    // This includes all intermediate states (processing, failed, and published)
    const completionStates = [
      "content_generating",
      "content_failed",
      "draft_ready",
      "slides_generating",
      "slides_failed",
      "slides_ready",
      "checkout_pending",
      "checkout_failed",
      "publishing",
      "publish_failed",
      "ad_creating",
      "ad_failed",
      "unpublishing",
      "published_free",
      "published_paid",
      "published_trial",
      "published_inactive",
      "published_canceling",
    ];
    const isContentComplete = completionStates.includes(
      landingPage.compositeState || ""
    );
    const isJobComplete =
      landingPage.jobStatus === "completed" || isContentComplete;

    return {
      id: landingPage.id || jobId,
      status: isJobComplete ? "completed" : landingPage.jobStatus || "pending",
      progress: landingPage.jobProgress || 0,
      message: landingPage.jobMessage || null,
      error: landingPage.jobError || null,
      // Return the landing page as result when completed so redirects work properly
      result: isJobComplete ? landingPage : null,
      taskStates: landingPage.taskStates || null,
      compositeState: landingPage.compositeState || null,
    };
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get landing page by slug (public)
// Wrapped with React.cache() to deduplicate calls across layout and page in the same render
export const getLandingPageBySlug = cache(async function getLandingPageBySlug(
  slug: string
): Promise<JobLandingPageSchema> {
  try {
    return await clientApi.get<JobLandingPageSchema>(
      `/api/v1/job-landing-pages/${slug}`
    );
  } catch (error) {
    // Keeps the HTTP status, so the job page can tell closed from an outage
    return rethrowWithApiErrorCode(error, await handleApiError(error));
  }
});

/**
 * What an "is this job still open?" email link is asking about. Read-only.
 * @param token - Token from the email link
 */
export async function getJobOpenCheck(token: string): Promise<JobOpenCheck> {
  try {
    return await clientApi.get<JobOpenCheck>(
      `/api/v1/job-open-check/${encodeURIComponent(token)}`
    );
  } catch (error) {
    return rethrowWithApiErrorCode(error, await handleApiError(error));
  }
}

/**
 * Save the owner's answer. Answering "filled" takes the job offline.
 * @param token - Token from the email link
 * @param stillOpen - Whether the job is still open
 */
export async function answerJobOpenCheck(
  token: string,
  stillOpen: boolean
): Promise<{ result: JobOpenCheckOutcome }> {
  try {
    return await clientApi.post<{ result: JobOpenCheckOutcome }>(
      `/api/v1/job-open-check/${encodeURIComponent(token)}`,
      { stillOpen }
    );
  } catch (error) {
    return rethrowWithApiErrorCode(error, await handleApiError(error));
  }
}

// Get landing page by id (authenticated)
export async function getLandingPageById(
  id: string
): Promise<JobLandingPageSchema> {
  try {
    return await clientApi.getAuth<JobLandingPageSchema>(
      `/api/admin/job-landing-pages-id/${id}`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Subscription renewal response type
export interface SubscriptionRenewalResponse {
  renewalDate: string | null;
  subscriptionId: string | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
}

// Get subscription renewal info for a landing page
export async function getSubscriptionRenewal(
  landingPageId: string
): Promise<SubscriptionRenewalResponse> {
  try {
    return await clientApi.getAuth<SubscriptionRenewalResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/subscription-renewal`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// List published job landing pages (public)
// Note: API returns full JobLandingPageSchema with companyLogo, promotionMedia, etc.
export async function listPublishedJobLandingPages(
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<JobLandingPageSchema>> {
  try {
    return await clientApi.get<PaginatedResponse<JobLandingPageSchema>>(
      `/api/v1/job-landing-pages?page=${page}&page_size=${pageSize}&status=published`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Search published job landing pages (public) - thin response for open positions
export async function searchPublicJobs(
  query?: string,
  page: number = 1,
  pageSize: number = 50
): Promise<PublicSearchResponse> {
  try {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (query) {
      params.set("q", query);
    }
    return await clientApi.get<PublicSearchResponse>(
      `/api/v1/job-landing-pages/search?${params.toString()}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Get a user's career page by slug (public) - returns paginated landing pages
export async function getCareerPageBySlug(
  slug: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<JobLandingPageSchema>> {
  try {
    return await clientApi.get<PaginatedResponse<JobLandingPageSchema>>(
      `/api/v1/career-pages/${encodeURIComponent(slug)}?page=${page}&page_size=${pageSize}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Get basic career page info by slug (public). Keeps the HTTP status, so
// callers can tell an unknown slug (404) from an outage.
export async function getCareerPageInfo(
  slug: string
): Promise<CareerPageInfoResponse> {
  try {
    return await clientApi.get<CareerPageInfoResponse>(
      `/api/v1/career-pages/${encodeURIComponent(slug)}/info`
    );
  } catch (error) {
    return rethrowWithApiErrorCode(error, await handleApiError(error));
  }
}

// Get image (public)
export async function getImage(imageId: string): Promise<Blob> {
  try {
    return await clientApi.get<Blob>(`/api/v1/images/${imageId}`);
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Health check
export async function healthCheck() {
  try {
    return await clientApi.get("/health");
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// ============================================================================
// AUTHENTICATED API FUNCTIONS (Requires authentication)
// ============================================================================

// Submit job landing page request
export async function submitJobLandingPage(
  data: JobRequest
): Promise<JobSchema> {
  try {
    const requestBody = {
      companyUrl: data.companyUrl,
      jobAdUrl: data.jobAdUrl,
      model: data.model || "gpt",
    };

    return await clientApi.postAuth<JobSchema>(
      "/api/admin/job-landing-pages",
      requestBody,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Change landing page ownership (admin only)
export interface ChangeOwnershipRequest {
  newOwnerEmail: string;
}

export async function changeLandingPageOwnership(
  landingPageId: string,
  newOwnerEmail: string
): Promise<Record<string, unknown>> {
  try {
    return await clientApi.putAuth<Record<string, unknown>>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/change-ownership`,
      { newOwnerEmail }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

export interface LandingPageJobResponse {
  jobId: string | null;
}

export async function getLandingPageJob(
  landingPageId: string
): Promise<LandingPageJobResponse> {
  try {
    return await clientApi.getAuth<LandingPageJobResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/job`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

export async function getLandingPageJobServer(
  landingPageId: string
): Promise<LandingPageJobResponse> {
  try {
    return await serverApi.getAuth<LandingPageJobResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/job`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    throw new Error(errorMessage);
  }
}

// Generate slides for landing page and send email (authenticated)
// ============================================================================
// NEW STATE MACHINE ACTION ENDPOINTS
// These are the preferred methods for state transitions.
// ============================================================================

// Response type for action endpoints
export interface ActionResponse {
  success: boolean;
  message: string;
  composite_state: string | null;
  checkout_url?: string | null;
}

// Save landing page data only (no state change) - partial update
// This is the preferred method for autosave
/** Error thrown when a version conflict occurs during save */
export class SaveConflictError extends Error {
  public readonly serverVersion: number;
  public readonly serverUpdatedAt: string | null;

  constructor(serverVersion: number, serverUpdatedAt: string | null) {
    super("Version conflict: another user has modified this page");
    this.name = "SaveConflictError";
    this.serverVersion = serverVersion;
    this.serverUpdatedAt = serverUpdatedAt;
  }
}

// Accepts partial data - only sends fields that need to be updated
export async function saveLandingPage(
  landingPageId: string,
  updateData: Partial<JobLandingPageSchema>
): Promise<JobLandingPageSchema> {
  try {
    return await clientApi.patchAuth<JobLandingPageSchema>(
      `/api/admin/job-landing-pages/${landingPageId}`,
      updateData,
      { useNextApi: true }
    );
  } catch (error) {
    // Check for 409 Conflict (version conflict)
    const status = (error as { status?: number })?.status;
    if (status === 409) {
      // Read conflict details from the attached response body (preferred)
      // or fall back to parsing the error message string
      const body = (error as { body?: Record<string, unknown> })?.body;
      let serverVersion = 1;
      let serverUpdatedAt: string | null = null;
      if (body) {
        serverVersion = (body.serverVersion as number) || 1;
        serverUpdatedAt = (body.serverUpdatedAt as string) || null;
      }
      throw new SaveConflictError(serverVersion, serverUpdatedAt);
    }

    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

/**
 * Draft fresh CV questions with AI (admin only). Nothing is saved; the
 * caller puts the draft into the editor as an unsaved, undoable change.
 */
export async function generateCvQuestionsAction(
  landingPageId: string
): Promise<FunnelQuestion[]> {
  try {
    return await clientApi.postAuth<FunnelQuestion[]>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/generate-cv-questions`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Publish landing page for free (no ads)
export async function publishFreeAction(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/publish-free`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Publish landing page with paid ads
// Returns ActionResponse on success, or PaymentRequiredResponse if payment needed
export async function publishPaidAction(
  landingPageId: string
): Promise<ActionResponse | PaymentRequiredResponse> {
  try {
    return await clientApi.postAuth<ActionResponse | PaymentRequiredResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/publish-paid`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Activate Bexio-billed package (bypasses Stripe checkout)
export async function activateBexioAction(
  landingPageId: string,
  priceName: string,
  currency: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/activate-bexio`,
      { price_name: priceName, currency },
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Cancel Bexio-billed package
export async function cancelBexioAction(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/cancel-bexio`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Reactivate Bexio-billed package (un-cancel)
export async function reactivateBexioAction(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/reactivate-bexio`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Unpublish landing page
export async function unpublishAction(
  landingPageId: string,
  options?: { onlyAds?: boolean }
): Promise<ActionResponse> {
  try {
    const query = options?.onlyAds ? "?onlyAds=true" : "";
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/unpublish${query}`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

/**
 * Resume a paused page's existing Meta ads (no checkout, no new ads).
 * @param landingPageId - Landing page whose ads the owner paused
 */
export async function resumeAdsAction(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/resume-ads`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Reactivate a cancelled subscription response type
export interface ReactivateSubscriptionResponse {
  success: boolean;
  message: string;
  compositeState?: string | null;
}

// Reactivate a cancelled subscription (undo cancel_at_period_end)
export async function reactivateSubscriptionAction(
  landingPageId: string
): Promise<ReactivateSubscriptionResponse> {
  try {
    return await clientApi.postAuth<ReactivateSubscriptionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/reactivate-subscription`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Generate slides using new action endpoint
// Supports optional feedback parameter for user-directed regeneration
export async function generateSlidesAction(
  landingPageId: string,
  options?: { feedback?: string; groupIndex?: number }
): Promise<ActionResponse & { job_id?: string }> {
  try {
    const body: { feedback?: string; group_index?: number } = {};
    if (options?.feedback) {
      body.feedback = options.feedback;
    }
    if (options?.groupIndex !== undefined) {
      body.group_index = options.groupIndex;
    }
    return await clientApi.postAuth<ActionResponse & { job_id?: string }>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/generate-slides`,
      body,
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Retry a failed action
export async function retryAction(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await clientApi.postAuth<ActionResponse>(
      `/api/admin/job-landing-pages/${landingPageId}/actions/retry`,
      {},
      { useNextApi: true }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// END NEW STATE MACHINE ACTION ENDPOINTS
// ============================================================================

// Resend verification email (no authentication required)
export async function resendVerificationEmail(
  email: string
): Promise<Record<string, unknown>> {
  try {
    return await clientApi.post<Record<string, unknown>>(
      `/api/resend-verification`,
      { email },
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}
// Delete landing page
export async function deleteLandingPage(landingPageId: string): Promise<void> {
  try {
    await clientApi.deleteAuth(
      `/api/admin/job-landing-pages/${landingPageId}`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// List all job landing pages (authenticated)
export async function listAllJobLandingPages(
  page: number = 1,
  pageSize: number = 20,
  filter?:
    | "published_paid"
    | "published_trialing"
    | "published_without_subscription"
    | "processing"
    | "draft"
    | "failed"
    | "deleted"
): Promise<PaginatedResponse<JobListing>> {
  try {
    const url =
      `/api/admin/job-landing-pages?page=${page}&page_size=${pageSize}` +
      (filter ? `&filter=${encodeURIComponent(filter)}` : "");
    return await clientApi.getAuth<PaginatedResponse<JobListing>>(url, true);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Search landing pages with unified search and status filtering (authenticated)
export async function searchLandingPages(
  q?: string,
  status?: SearchStatus,
  page: number = 1,
  pageSize: number = 100
): Promise<SearchResponse> {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (status) params.append("status", status);
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());

    const url = `/api/admin/job-landing-pages/search?${params.toString()}`;
    return await clientApi.getAuth<SearchResponse>(url, true);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of search landing pages
export async function searchLandingPagesServer(
  q?: string,
  status?: SearchStatus,
  page: number = 1,
  pageSize: number = 100
): Promise<SearchResponse> {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (status) params.append("status", status);
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());

    const url = `/admin/api/v1/job-landing-pages/search?${params.toString()}`;
    return await serverApi.getAuth<SearchResponse>(url);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Response from backend for signed image upload URL
interface ImageUploadUrlResponse {
  image_id: string;
  upload_url: string;
  image_url: string;
  content_type: string;
  expires_in_minutes: number;
}

// Upload image using presigned URL (direct to GCS, no backend processing)
export async function uploadImage(
  file: File,
  imageType?: "logo" | "slide" | "hero"
): Promise<ImageUploadResponse> {
  try {
    // Get auth token from session
    const { getSession } = await import("next-auth/react");
    const session = (await getSession()) as { accessToken?: string } | null;
    const token = session?.accessToken;

    if (!token) {
      throw new Error("Authentication failed. Please sign in again.");
    }

    // Get backend URL using the environment-aware getter
    const { getApiBaseUrl } = await import("../utils/index");
    const backendUrl = getApiBaseUrl();

    const contentType = file.type || "image/png";

    // 1. Get signed upload URL from backend
    const urlResponse = await fetch(
      `${backendUrl}/admin/api/v1/images/upload-url?content_type=${encodeURIComponent(contentType)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!urlResponse.ok) {
      const errorText = await urlResponse.text();
      throw new Error(
        errorText || `Failed to get upload URL: ${urlResponse.status}`
      );
    }

    const { upload_url, image_url, image_id }: ImageUploadUrlResponse =
      await urlResponse.json();

    // 2. Upload directly to GCS (bypasses API server size limits)
    const uploadResponse = await fetch(upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const errorBody = await uploadResponse.text().catch(() => "");
      throw new Error(
        `Upload failed: ${uploadResponse.status}${errorBody ? ` - ${errorBody.slice(0, 200)}` : ""}`
      );
    }

    // 3. Return response matching ImageUploadResponse interface
    // Use the GCS URL directly instead of the /api/v1/images/:id path
    return {
      id: image_id,
      filename: file.name,
      contentType: contentType,
      createdAt: new Date().toISOString(),
      url: image_url,
    };
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Response from backend for signed upload URL
interface VideoUploadUrlResponse {
  video_id: string;
  upload_url: string;
  video_url: string;
  content_type: string;
  expires_in_minutes: number;
}

// Upload video for social media campaigns (requires authentication)
// Uses signed URL pattern: get signed URL from backend, upload directly to GCS
export async function uploadVideo(file: File): Promise<VideoUploadResponse> {
  try {
    // Get auth token from session
    const { getSession } = await import("next-auth/react");
    const session = (await getSession()) as { accessToken?: string } | null;
    const token = session?.accessToken;

    if (!token) {
      throw new Error("Authentication failed. Please sign in again.");
    }

    // Get backend URL using the environment-aware getter (handles localhost vs prod)
    const { getApiBaseUrl } = await import("../utils/index");
    const backendUrl = getApiBaseUrl();

    const contentType = file.type || "video/mp4";

    // 1. Get signed upload URL from backend
    const urlResponse = await fetch(
      `${backendUrl}/admin/api/v1/videos/upload-url?content_type=${encodeURIComponent(contentType)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!urlResponse.ok) {
      const errorText = await urlResponse.text();
      throw new Error(
        errorText || `Failed to get upload URL: ${urlResponse.status}`
      );
    }

    const { upload_url, video_url, video_id }: VideoUploadUrlResponse =
      await urlResponse.json();

    // 2. Upload directly to GCS (bypasses API server size limits)
    const uploadResponse = await fetch(upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`GCS upload failed: ${uploadResponse.status}`);
    }

    // 3. Return the video URL for use in the landing page
    return {
      url: video_url,
      video_id: video_id,
    };
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Delete video from landing page (requires authentication)
export async function deleteVideo(landingPageId: string): Promise<void> {
  try {
    await clientApi.deleteAuth(
      `/api/proxy/admin/api/v1/landing-pages/${landingPageId}/video`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Upload CV (public, no auth), with the applicant's optional profile details
export async function uploadCv(
  applicationId: string,
  file: File,
  details: { skills?: string; expectedSalary?: string } = {}
): Promise<unknown> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    for (const [key, value] of Object.entries(details)) {
      if (value?.trim()) formData.append(key, value.trim());
    }
    const endpoint = `/upload/${applicationId}`;
    return await clientApi.uploadFilePublic<unknown>(endpoint, formData, false);
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Upload job ad file (public, no auth)
export interface JobAdUploadResponse {
  fileId: string;
  filename: string;
  contentType: string;
  size: number;
  message: string;
}

export async function uploadJobAd(file: File): Promise<JobAdUploadResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const endpoint = `/api/v1/job-ads/upload`;
    return await clientApi.uploadFilePublic<JobAdUploadResponse>(
      endpoint,
      formData,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// ============================================================================
// STRIPE PAYMENT API FUNCTIONS (Requires authentication)
// ============================================================================

// Create Stripe checkout session
export async function createCheckoutSession(
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> {
  try {
    return await clientApi.postAuth<CheckoutSessionResponse>(
      "/api/stripe/create-checkout-session",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Create customer portal session
export async function createCustomerPortalSession(
  request: CustomerPortalRequest
): Promise<CustomerPortalResponse> {
  try {
    return await clientApi.postAuth<CustomerPortalResponse>(
      "/api/stripe/create-portal-session",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// ADMIN API FUNCTIONS (Requires admin authentication)
// ============================================================================

// Get daily reports for admin
export async function getDailyReports(
  days: number = 7,
  selection: { owner?: string; slug?: string } = {}
): Promise<{
  startDate: string;
  endDate: string;
  days: number;
  dates: string[];
  series: Array<{
    section: string;
    metrics: Array<{
      metric: string;
      data: Array<{
        date: string;
        value: number;
      }>;
    }>;
  }>;
  owners: string[];
  slugs: string[];
}> {
  // Compact: owner/job series only for the selection. The full response grows
  // with every job and day and broke the proxy's response limit past ~90 days.
  const params = new URLSearchParams({ days: String(days), compact: "true" });
  if (selection.owner) params.set("owner", selection.owner);
  if (selection.slug) params.set("slug", selection.slug);
  try {
    return await clientApi.getAuth<{
      startDate: string;
      endDate: string;
      days: number;
      dates: string[];
      series: Array<{
        section: string;
        metrics: Array<{
          metric: string;
          data: Array<{
            date: string;
            value: number;
          }>;
        }>;
      }>;
      owners: string[];
      slugs: string[];
    }>(`/api/admin/reporting/daily?${params}`, true);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// B2B Reporting API functions (admin only)
export async function getB2BSnapshots(
  days: number = 30,
  source: string = "all",
  entityType: string = "all"
): Promise<{
  start_date: string;
  end_date: string;
  days: number;
  count: number;
  snapshots: unknown[];
}> {
  try {
    const params = new URLSearchParams({
      days: String(days),
      source,
      entity_type: entityType,
    });
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/snapshots?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BSearchQueries(
  days: number = 30,
  limit: number = 100,
  entityId?: string
): Promise<{
  start_date: string;
  end_date: string;
  count: number;
  queries: unknown[];
}> {
  try {
    const params = new URLSearchParams({
      days: String(days),
      limit: String(limit),
    });
    if (entityId) params.append("entity_id", entityId);
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/search-queries?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BAdPerformance(
  days: number = 30,
  campaignId?: string
): Promise<{
  start_date: string;
  end_date: string;
  count: number;
  ads: unknown[];
}> {
  try {
    const params = new URLSearchParams({ days: String(days) });
    if (campaignId) params.append("campaign_id", campaignId);
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/ad-performance?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BContentMetrics(
  days: number = 30,
  blogPostId?: string
): Promise<{
  start_date: string;
  end_date: string;
  count: number;
  metrics: unknown[];
}> {
  try {
    const params = new URLSearchParams({ days: String(days) });
    if (blogPostId) params.append("blog_post_id", blogPostId);
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/content-metrics?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BBlogPosts(): Promise<{
  count: number;
  blog_posts: unknown[];
}> {
  try {
    return await clientApi.getAuth(`/api/admin/b2b-reporting/blog-posts`, true);
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BEntityDetail(
  entityType: string,
  entityId: string,
  days: number = 30
): Promise<Record<string, unknown>> {
  try {
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/entity/${entityType}/${entityId}?days=${days}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BIndexCoverage(days: number = 90): Promise<{
  start_date: string;
  end_date: string;
  count: number;
  coverage: unknown[];
}> {
  try {
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/index-coverage?days=${days}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BSubscriptionRevenue(days: number = 90): Promise<{
  start_date: string;
  end_date: string;
  days: number;
  total_revenue_cents: number;
  daily: unknown[];
  by_owner: unknown[];
}> {
  try {
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/subscription-revenue?days=${days}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getActivityStream(
  days: number = 30,
  eventType: string = "",
  entityType: string = "",
  limit: number = 100,
  offset: number = 0
): Promise<{
  start_date: string;
  end_date: string;
  total: number;
  limit: number;
  offset: number;
  count: number;
  events: unknown[];
}> {
  try {
    const params = new URLSearchParams({
      days: String(days),
      limit: String(limit),
      offset: String(offset),
    });
    if (eventType) params.set("event_type", eventType);
    if (entityType) params.set("entity_type", entityType);
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/activity-stream?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BInsights(): Promise<{
  content: string | null;
  updated_at: string | null;
  message?: string;
}> {
  try {
    return await clientApi.getAuth(`/api/admin/b2b-reporting/insights`, true);
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BEventCompare(): Promise<{
  pixel_updated_at: string | null;
  ga4_updated_at: string | null;
  mapped: Array<{
    display_name: string;
    meta_event: string;
    ga4_event: string;
    meta_count: number;
    ga4_count: number;
    diff: number;
    diff_pct: number | null;
  }>;
  meta_only: Array<{ event: string; count: number }>;
  ga4_only: Array<{ event: string; count: number }>;
}> {
  try {
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/event-compare`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BTrafficSources(
  days: number = 30,
  source?: string,
  medium?: string
): Promise<{
  start_date: string;
  end_date: string;
  count: number;
  sources: unknown[];
}> {
  try {
    const params = new URLSearchParams({ days: String(days) });
    if (source) params.append("source", source);
    if (medium) params.append("medium", medium);
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/traffic-sources?${params}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

export async function getB2BConversionAttribution(days: number = 30): Promise<{
  total_signups: number;
  total_subscriptions: number;
  total_revenue_cents: number;
  total_applicants: number;
  total_cvs: number;
  by_source: unknown[];
}> {
  try {
    return await clientApi.getAuth(
      `/api/admin/b2b-reporting/conversion-attribution?days=${days}`,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// List all emails (admin only)
export async function listEmails(
  page: number = 1,
  pageSize: number = 20,
  emailType?: string
): Promise<PaginatedResponse<EmailSchema>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (emailType) params.set("email_type", emailType);

    return await clientApi.getAuth<PaginatedResponse<EmailSchema>>(
      `/api/admin/emails?${params.toString()}`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get email details by ID (admin only)
export async function getEmailById(emailId: string): Promise<EmailSchema> {
  try {
    return await clientApi.getAuth<EmailSchema>(
      `/api/admin/emails/${emailId}`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// SERVER-SIDE API FUNCTIONS (For API routes)
// ============================================================================

// Server-side version of list all job landing pages
export async function listAllJobLandingPagesServer(
  page: number = 1,
  pageSize: number = 20,
  filter?:
    | "published_paid"
    | "published_trialing"
    | "published_without_subscription"
    | "processing"
    | "draft"
    | "failed"
    | "deleted"
): Promise<PaginatedResponse<JobListing>> {
  try {
    const url =
      `/admin/api/v1/job-landing-pages?page=${page}&page_size=${pageSize}` +
      (filter ? `&filter=${encodeURIComponent(filter)}` : "");
    return await serverApi.getAuth<PaginatedResponse<JobListing>>(url);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of submit job landing page
export async function submitJobLandingPageServer(
  data: JobRequest
): Promise<JobSchema> {
  try {
    const requestBody = {
      companyUrl: data.companyUrl,
      jobAdUrl: data.jobAdUrl,
      model: data.model || "gpt",
    };

    return await serverApi.postAuth<JobSchema>(
      "/admin/api/v1/job-landing-pages",
      requestBody
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version: Change landing page ownership (admin only)
export async function changeLandingPageOwnershipServer(
  landingPageId: string,
  newOwnerEmail: string
): Promise<Record<string, unknown>> {
  try {
    return await serverApi.putAuth<Record<string, unknown>>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/change-ownership`,
      { newOwnerEmail }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of delete landing page
export async function deleteLandingPageServer(
  landingPageId: string
): Promise<void> {
  try {
    await serverApi.deleteAuth(
      `/admin/api/v1/job-landing-pages/${landingPageId}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of get landing page
export async function getLandingPageServer(
  landingPageId: string
): Promise<JobLandingPageSchema> {
  try {
    return await serverApi.getAuth<JobLandingPageSchema>(
      `/admin/api/v1/job-landing-pages/${landingPageId}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// SERVER-SIDE NEW STATE MACHINE ACTION ENDPOINTS
// ============================================================================

// Server-side: Save landing page data (partial update)
export async function saveLandingPageServer(
  landingPageId: string,
  updateData: Partial<JobLandingPageSchema>
): Promise<JobLandingPageSchema> {
  try {
    return await serverApi.patchAuth<JobLandingPageSchema>(
      `/admin/api/v1/job-landing-pages/${landingPageId}`,
      updateData
    );
  } catch (error) {
    // Check for 409 Conflict (version conflict)
    const status = (error as { status?: number })?.status;
    if (status === 409) {
      const body = (error as { body?: Record<string, unknown> })?.body;
      let serverVersion = 1;
      let serverUpdatedAt: string | null = null;
      if (body) {
        serverVersion = (body.serverVersion as number) || 1;
        serverUpdatedAt = (body.serverUpdatedAt as string) || null;
      }
      throw new SaveConflictError(serverVersion, serverUpdatedAt);
    }

    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Publish landing page for free (no ads)
export async function publishFreeActionServer(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/publish-free`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Publish landing page with paid ads
export async function publishPaidActionServer(
  landingPageId: string
): Promise<ActionResponse | PaymentRequiredResponse> {
  try {
    return await serverApi.postAuth<ActionResponse | PaymentRequiredResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/publish-paid`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    // Preserve the machine-readable code/status so the proxy route can forward
    // them and the UI can render localised copy instead of English.
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Server-side: Unpublish landing page using new action endpoint
export async function unpublishActionServer(
  landingPageId: string,
  options?: { onlyAds?: boolean }
): Promise<ActionResponse> {
  try {
    const query = options?.onlyAds ? "?onlyAds=true" : "";
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/unpublish${query}`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Server-side: Resume a paused page's existing Meta ads
export async function resumeAdsActionServer(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/resume-ads`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Server-side: Reactivate a cancelled subscription
export async function reactivateSubscriptionActionServer(
  landingPageId: string
): Promise<ReactivateSubscriptionResponse> {
  try {
    return await serverApi.postAuth<ReactivateSubscriptionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/reactivate-subscription`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Activate Bexio-billed package
export async function activateBexioActionServer(
  landingPageId: string,
  priceName: string,
  currency: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/activate-bexio`,
      { price_name: priceName, currency }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    return rethrowWithApiErrorCode(error, errorMessage);
  }
}

// Server-side: Cancel Bexio-billed package
export async function cancelBexioActionServer(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/cancel-bexio`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Reactivate Bexio-billed package (un-cancel)
export async function reactivateBexioActionServer(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/reactivate-bexio`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Generate slides using new action endpoint
// Supports optional feedback parameter for user-directed regeneration
export async function generateSlidesActionServer(
  landingPageId: string,
  options?: { feedback?: string; groupIndex?: number }
): Promise<ActionResponse & { job_id?: string }> {
  try {
    const body: { feedback?: string; group_index?: number } = {};
    if (options?.feedback) {
      body.feedback = options.feedback;
    }
    if (options?.groupIndex !== undefined) {
      body.group_index = options.groupIndex;
    }
    return await serverApi.postAuth<ActionResponse & { job_id?: string }>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/generate-slides`,
      body
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Retry a failed action
export async function retryActionServer(
  landingPageId: string
): Promise<ActionResponse> {
  try {
    return await serverApi.postAuth<ActionResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/actions/retry`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// END SERVER-SIDE NEW STATE MACHINE ACTION ENDPOINTS
// ============================================================================

// Server-side version of upload image
export async function uploadImageServer(
  formData: FormData,
  imageType?: "logo" | "slide" | "hero"
): Promise<ImageUploadResponse> {
  try {
    const endpoint = `/admin/api/v1/images${
      imageType ? `?image_type=${imageType}` : ""
    }`;
    return await serverApi.uploadFile<ImageUploadResponse>(endpoint, formData);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of create checkout session
export async function createCheckoutSessionServer(
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> {
  try {
    return await serverApi.postAuth<CheckoutSessionResponse>(
      "/api/stripe/create-checkout-session",
      request
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of create customer portal session
export async function createCustomerPortalSessionServer(
  request: CustomerPortalRequest
): Promise<CustomerPortalResponse> {
  try {
    return await serverApi.postAuth<CustomerPortalResponse>(
      "/api/stripe/create-portal-session",
      request
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// USER MANAGEMENT API FUNCTIONS (Requires authentication)
// ============================================================================

// Get current user info
export async function getCurrentUserInfo(): Promise<UserResponse> {
  try {
    return await clientApi.getAuth<UserResponse>("/api/auth/me", true);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Update current user info
export async function updateCurrentUserInfo(
  updateData: UserUpdateRequest
): Promise<UserResponse> {
  try {
    return await clientApi.postAuth<UserResponse>(
      "/api/auth/me",
      updateData,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get ATS types (no authentication required)
export async function getAtsTypes(): Promise<AtsType[]> {
  try {
    return await clientApi.get<AtsType[]>("/auth/ats-types");
  } catch (error) {
    const errorMessage = await handleApiError(error);
    throw new Error(errorMessage);
  }
}

// Get ATS published positions (authenticated)
export async function getATSPublishedPositions(): Promise<
  ATSPublishedPosition[]
> {
  try {
    return await clientApi.getAuth<ATSPublishedPosition[]>(
      "/api/admin/ats/published-positions",
      false // Use backend API directly
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Search ATS published positions (authenticated)
export async function searchATSPublishedPositions(
  query: string
): Promise<ATSPublishedPosition[]> {
  try {
    const params = new URLSearchParams({ q: query });
    return await clientApi.getAuth<ATSPublishedPosition[]>(
      `/api/admin/ats/published-positions/search?${params.toString()}`,
      false // Use backend API directly
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get ATS positions (authenticated)
export async function getATSPositions(): Promise<ATSPosition[]> {
  try {
    return await clientApi.getAuth<ATSPosition[]>(
      "/api/admin/ats/positions",
      false // Use backend API directly
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Activate career page (authenticated)
export async function activateCareerPage(
  request: ActivateCareerPageRequest
): Promise<ActivateCareerPageResponse> {
  try {
    return await clientApi.postAuth<ActivateCareerPageResponse>(
      "/api/auth/career-page/activate",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Update career page slug (authenticated)
export async function updateCareerPageSlug(
  request: UpdateCareerPageSlugRequest
): Promise<UpdateCareerPageSlugResponse> {
  try {
    return await clientApi.putAuth<UpdateCareerPageSlugResponse>(
      "/api/auth/career-page/slug",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of get current user info
export async function getCurrentUserInfoServer(): Promise<UserResponse> {
  try {
    return await serverApi.getAuth<UserResponse>("/api/auth/me", true);
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of update current user info
export async function updateCurrentUserInfoServer(
  updateData: UserUpdateRequest
): Promise<UserResponse> {
  try {
    return await serverApi.postAuth<UserResponse>(
      "/api/auth/me",
      updateData,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// APPLICANT EMAIL API FUNCTIONS (Requires authentication)
// ============================================================================

// Send applicant email
export async function sendApplicantEmail(
  data: SendApplicantEmailRequest
): Promise<SendApplicantEmailResponse> {
  try {
    return await clientApi.postAuth<SendApplicantEmailResponse>(
      "/admin/api/v1/send-applicant-email",
      data,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of send applicant email
export async function sendApplicantEmailServer(
  data: SendApplicantEmailRequest
): Promise<SendApplicantEmailResponse> {
  try {
    return await serverApi.postAuth<SendApplicantEmailResponse>(
      "/admin/api/v1/send-applicant-email",
      data
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version for public access (no authentication required)
export async function sendApplicantEmailServerPublic(
  data: SendApplicantEmailRequest
): Promise<SendApplicantEmailResponse> {
  try {
    // This specific admin endpoint is configured to allow public access
    return await serverApi.post<SendApplicantEmailResponse>(
      "/admin/api/v1/send-applicant-email",
      data
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Server-side: Send CV upload reminder (authenticated)
export async function sendCvReminderServer(
  request: SendCVReminderRequest
): Promise<Record<string, unknown>> {
  try {
    return await serverApi.postAuth<Record<string, unknown>>(
      "/admin/api/v1/send-cv-reminder",
      request
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side: Send interview invite (authenticated)
export async function sendInterviewInviteServer(
  request: SendInterviewInviteRequest
): Promise<Record<string, unknown>> {
  try {
    return await serverApi.postAuth<Record<string, unknown>>(
      "/admin/api/v1/send-interview-invite",
      request
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// ============================================================================
// APPLICANTS (Requires authentication)
// ============================================================================

// Fetch applicants for a landing page (paginated)
export async function getApplicants(
  landingPageId: string,
  page: number = 1,
  size: number = 20,
  includeRejected: boolean = false
): Promise<PaginatedResponse<Applicant>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      includeRejected: includeRejected.toString(),
    });

    return await clientApi.getAuth<PaginatedResponse<Applicant>>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/applicants?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get landing page analytics
export async function getLandingPageAnalytics(
  landingPageId: string
): Promise<LandingPageAnalytics> {
  try {
    return await clientApi.getAuth<LandingPageAnalytics>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/analytics`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Send CV upload reminder (authenticated)
export async function sendCvReminder(
  request: SendCVReminderRequest
): Promise<Record<string, unknown>> {
  try {
    // Use Next.js API route to avoid CORS/adblock issues
    return await clientApi.postAuth<Record<string, unknown>>(
      "/api/admin/send-cv-reminder",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Send interview invite (authenticated)
export async function sendInterviewInvite(
  request: SendInterviewInviteRequest
): Promise<SendInterviewInviteResponse> {
  try {
    // Use Next.js API route to avoid CORS/adblock issues
    return await clientApi.postAuth<SendInterviewInviteResponse>(
      "/api/admin/send-interview-invite",
      request,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Get CV file
export async function getCvFile(cvId: string): Promise<Blob> {
  try {
    const result = await clientApi.getAuth<Blob>(`/api/v1/cvs/${cvId}`);

    // Ensure we have a proper Blob
    if (result instanceof Blob) {
      return result;
    } else {
      // If it's not a Blob, try to convert it
      return new Blob([result as ArrayBuffer | string], {
        type: "application/pdf",
      });
    }
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Replace applicant CV and trigger new analysis (authenticated)
export interface ReplaceCvResponse {
  message: string;
  applicant_id: string;
  upload_link_id: string;
}

export async function replaceApplicantCv(
  applicantId: string,
  file: File
): Promise<ReplaceCvResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return await clientApi.uploadFilePutAuth<ReplaceCvResponse>(
      `/api/v1/applicants/${applicantId}/cv`,
      formData,
      false
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Reject applicant
export async function rejectApplicant(
  applicantId: string,
  rejectionMessage: string,
  noMessage: boolean = false
): Promise<{ message: string }> {
  try {
    return await clientApi.postAuth<{ message: string }>(
      `/api/v1/applicants/${applicantId}/reject`,
      { rejectionMessage, noMessage }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Mark applicant as hired
export async function hireApplicant(
  applicantId: string,
  note?: string
): Promise<{ message: string }> {
  try {
    return await clientApi.postAuth<{ message: string }>(
      `/api/v1/applicants/${applicantId}/hire`,
      { note: note ?? null }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Undo an accidental hire — moves applicant back to invited
export async function unhireApplicant(
  applicantId: string
): Promise<{ message: string }> {
  try {
    return await clientApi.postAuth<{ message: string }>(
      `/api/v1/applicants/${applicantId}/unhire`,
      {}
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Park an applicant in the "keep for later" state (not hired, not rejected)
export async function keepApplicantForLater(
  applicantId: string,
  note?: string
): Promise<{ message: string }> {
  try {
    return await clientApi.postAuth<{ message: string }>(
      `/api/v1/applicants/${applicantId}/keep-for-later`,
      { note: note ?? null }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Move an applicant out of "kept for later" back into the normal flow.
// targetStatus must be one of "inbound" | "cv_analyzed" | "invited"; when
// omitted, the backend returns the applicant to the status it held before
// being parked. The response carries the resulting status either way.
export async function restoreApplicant(
  applicantId: string,
  targetStatus?: string
): Promise<{ message: string; status: string }> {
  try {
    return await clientApi.postAuth<{ message: string; status: string }>(
      `/api/v1/applicants/${applicantId}/restore`,
      { targetStatus: targetStatus ?? null }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Update applicant notes
export async function updateApplicantNotes(
  applicantId: string,
  notes: string
): Promise<Applicant> {
  try {
    return await clientApi.putAuth<Applicant>(
      `/admin/api/v1/applicants/${applicantId}/notes`,
      { notes }
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version
export async function getApplicantsServer(
  landingPageId: string,
  page: number = 1,
  size: number = 20,
  includeRejected: boolean = false
): Promise<PaginatedResponse<Applicant>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      includeRejected: includeRejected.toString(),
    });

    return await serverApi.getAuth<PaginatedResponse<Applicant>>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/applicants?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Resend verification email (server-side)
export async function resendVerificationEmailServer(
  email: string
): Promise<Record<string, unknown>> {
  try {
    return await serverApi.post<Record<string, unknown>>(
      `/api/resend-verification`,
      { email }
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Verify email (no authentication required)
export async function verifyEmail(
  verificationId: string
): Promise<EmailVerificationResponse> {
  try {
    return await clientApi.post<EmailVerificationResponse>(
      `/api/verify/${verificationId}`,
      undefined,
      true
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Verify email (server-side)
export async function verifyEmailServer(
  verificationId: string
): Promise<EmailVerificationResponse> {
  try {
    return await serverApi.post<EmailVerificationResponse>(
      `/api/verify/${verificationId}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// ============================================================================
// CONTACT PURCHASE API FUNCTIONS (No authentication required)
// ============================================================================

// Purchase a contact (public, no auth)
export async function purchaseContact(
  contactId: string,
  purchaserEmail: string
): Promise<ContactPurchaseResponse> {
  try {
    return await clientApi.get<ContactPurchaseResponse>(
      `/api/v1/contacts/${contactId}/purchase?purchaser_email=${encodeURIComponent(purchaserEmail)}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Server-side version of purchase contact
export async function purchaseContactServer(
  contactId: string,
  purchaserEmail: string
): Promise<ContactPurchaseResponse> {
  try {
    return await serverApi.get<ContactPurchaseResponse>(
      `/api/v1/contacts/${contactId}/purchase?purchaser_email=${encodeURIComponent(purchaserEmail)}`
    );
  } catch (error) {
    throw new Error(await handleApiError(error));
  }
}

// Server-side version of list emails (admin only)
export async function listEmailsServer(
  page: number = 1,
  pageSize: number = 20,
  emailType?: string
): Promise<PaginatedResponse<EmailSchema>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (emailType) params.set("email_type", emailType);

    return await serverApi.getAuth<PaginatedResponse<EmailSchema>>(
      `/admin/api/v1/emails?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of get email by ID (admin only)
export async function getEmailByIdServer(
  emailId: string
): Promise<EmailSchema> {
  try {
    return await serverApi.getAuth<EmailSchema>(
      `/admin/api/v1/emails/${emailId}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// List all applicants (admin only)
export async function listAllApplicants(
  page: number = 1,
  pageSize: number = 20,
  status?: string,
  landingPageId?: string,
  ownerEmail?: string,
  createdAfter?: string,
  createdBefore?: string
): Promise<PaginatedResponse<ApplicantWithJobInfo>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (status) params.append("status", status);
    if (landingPageId) params.append("landing_page_id", landingPageId);
    if (ownerEmail) params.append("owner_email", ownerEmail);
    if (createdAfter) params.append("created_after", createdAfter);
    if (createdBefore) params.append("created_before", createdBefore);

    const url = `/api/admin/applicants?${params.toString()}`;
    const response = await clientApi.getAuth<PaginatedResponse<Applicant>>(
      url,
      true
    );
    return response;
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(`Failed to fetch applicants: ${errorMessage}`);
  }
}

// Server-side version of list all applicants (admin only)
export async function listAllApplicantsServer(
  page: number = 1,
  pageSize: number = 20,
  status?: string,
  landingPageId?: string,
  ownerEmail?: string,
  createdAfter?: string,
  createdBefore?: string
): Promise<PaginatedResponse<ApplicantWithJobInfo>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (status) params.append("status", status);
    if (landingPageId) params.append("landing_page_id", landingPageId);
    if (ownerEmail) params.append("owner_email", ownerEmail);
    if (createdAfter) params.append("created_after", createdAfter);
    if (createdBefore) params.append("created_before", createdBefore);

    return await serverApi.getAuth<PaginatedResponse<Applicant>>(
      `/admin/api/v1/applicants?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Get contact details by ID (admin only)
export async function getContactById(contactId: string): Promise<ContactApi> {
  try {
    return await clientApi.getAuth<ContactApi>(
      `/api/admin/contacts/${contactId}`,
      true
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(errorMessage);
  }
}

// Server-side version of get contact by ID (admin only)
export async function getContactByIdServer(
  contactId: string
): Promise<ContactApi> {
  try {
    return await serverApi.getAuth<ContactApi>(
      `/admin/api/v1/contacts/${contactId}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// List all contacts (admin only)
export async function listAllContacts(
  page: number = 1,
  pageSize: number = 20,
  status?: string,
  sourceType?: string
): Promise<PaginatedResponse<ContactApi>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (status) params.set("status", status);
    if (sourceType) params.set("source_type", sourceType);

    const url = `/api/admin/contacts?${params.toString()}`;
    const response = await clientApi.getAuth<PaginatedResponse<ContactApi>>(
      url,
      true
    );
    return response;
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(`Failed to fetch contacts: ${errorMessage}`);
  }
}

// Server-side version of list all contacts (admin only)
export async function listAllContactsServer(
  page: number = 1,
  pageSize: number = 20,
  status?: string,
  sourceType?: string
): Promise<PaginatedResponse<ContactApi>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (status) params.set("status", status);
    if (sourceType) params.set("source_type", sourceType);

    return await serverApi.getAuth<PaginatedResponse<ContactApi>>(
      `/admin/api/v1/contacts?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Get callsheet (admin only)
export async function getCallsheet(
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<CallsheetRowSchema>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const url = `/api/admin/callsheet?${params.toString()}`;
    const response = await clientApi.getAuth<
      PaginatedResponse<CallsheetRowSchema>
    >(url, true);
    return response;
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(`Failed to fetch callsheet: ${errorMessage}`);
  }
}

// Server-side version of get callsheet (admin only)
export async function getCallsheetServer(
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<CallsheetRowSchema>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    return await serverApi.getAuth<PaginatedResponse<CallsheetRowSchema>>(
      `/admin/api/v1/callsheet?${params.toString()}`
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Snooze landing page (admin only)
export async function snoozeLandingPage(
  landingPageId: string,
  numDays: number | null,
  close?: boolean
): Promise<SnoozeResponse> {
  try {
    const body: SnoozeRequest = { numDays, close };
    const url = `/api/admin/job-landing-pages/${landingPageId}/snooze`;
    const response = await clientApi.postAuth<SnoozeResponse>(url, body, true);
    return response;
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(`Failed to snooze landing page: ${errorMessage}`);
  }
}

// Server-side version of snooze landing page (admin only)
export async function snoozeLandingPageServer(
  landingPageId: string,
  numDays: number | null,
  close?: boolean
): Promise<SnoozeResponse> {
  try {
    const body: SnoozeRequest = { numDays, close };
    return await serverApi.postAuth<SnoozeResponse>(
      `/admin/api/v1/job-landing-pages/${landingPageId}/snooze`,
      body
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}

// Snooze contact (admin only)
export async function snoozeContact(
  contactId: string,
  numDays: number | null,
  close?: boolean
): Promise<SnoozeResponse> {
  try {
    const body: SnoozeRequest = { numDays, close };
    const url = `/api/admin/contacts/${contactId}/snooze`;
    const response = await clientApi.postAuth<SnoozeResponse>(url, body, true);
    return response;
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication failed. Please sign in again.");
    }
    throw new Error(`Failed to snooze contact: ${errorMessage}`);
  }
}

// Server-side version of snooze contact (admin only)
export async function snoozeContactServer(
  contactId: string,
  numDays: number | null,
  close?: boolean
): Promise<SnoozeResponse> {
  try {
    const body: SnoozeRequest = { numDays, close };
    return await serverApi.postAuth<SnoozeResponse>(
      `/admin/api/v1/contacts/${contactId}/snooze`,
      body
    );
  } catch (error) {
    const errorMessage = await handleApiError(error);
    if (errorMessage.includes("Authentication failed")) {
      throw new Error("Authentication required");
    }
    throw new Error(errorMessage);
  }
}
